import React, { useEffect } from 'react'
import { connect } from 'react-redux';
import { object, string } from 'yup';
import { useFormik } from 'formik';

// Store
import { Dispatch, RootState } from '../../store/store';

// Constants
import { LENGTH_MFA_CODE } from '../../constants/common';

// Types
import { AuthFormData } from '../../types/auth-form-data';
import { ElementSize } from '../../types/element-size';

// Components
import InputControl from '../InputControl/InputControl';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import InteractiveButton from '../InteractiveButton/InteractiveButton';

type MfaFormProps = {
  formData: AuthFormData,
  isMfaCompleted: boolean,
  isMfaSetup: boolean,
  isMfaCheck: boolean,
} & ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const MfaForm: React.FC<MfaFormProps> = (props) => {
  const { formData, mfaSetupData, verifyMfa, isMfaCompleted, mfaCheckData, checkMfa, isMfaSetup, isMfaCheck, isAuthLoading } = props;

  const loadingClassName = isAuthLoading ? 'flex-auto justify-center items-center' : 'w-full';
  const mfaFormClassName = `c-mfa-form flex  ${loadingClassName}`;

  const formik = useFormik({
    initialValues: {
      firstCode: '',
      secondCode: '',
    },
    validationSchema: object({
      firstCode: string().required('Required').length(LENGTH_MFA_CODE, `The MFA code must be ${LENGTH_MFA_CODE} digits long`),
      ...(isMfaSetup
        ? {
          secondCode: string()
            .required('Required')
            .length(LENGTH_MFA_CODE, `The MFA code must be ${LENGTH_MFA_CODE} digits long`)
            .test('code-match', 'You can\'t use same code twice', function (value) {
              return this.parent.firstCode !== value;
            }),
        }
        : {}),
    }),
    onSubmit: (values) => {
      if (isMfaSetup) {
        verifyMfa({ ...formData, session: mfaSetupData.session, code: values.firstCode });
      }

      if (isMfaCheck) {
        checkMfa({ code: values.firstCode, email: formData.email, session: mfaCheckData.session });
      }
    },
  });

  const isDisabledBtn = Boolean((formik.touched.secondCode && formik.errors.secondCode) || (formik.touched.firstCode && formik.errors.firstCode));

  useEffect(() => {
    if (isMfaCompleted && mfaCheckData && formik.values.secondCode) {
      checkMfa({ code: formik.values.secondCode, email: formData.email, session: mfaCheckData.session });
    }
  }, [isMfaCompleted, mfaCheckData, formik.values.secondCode]);

  return (
    <div className={mfaFormClassName}>
      {!isAuthLoading ? (
        <form className='c-mfa-codes-form__form flex flex-col flex-auto' onSubmit={formik.handleSubmit}>
          <div className={`c-mfa-codes-form__inputs w-full grid ${(isMfaSetup || isMfaCompleted) ? 'grid-rows-2 md:grid-rows-1 md:grid-cols-2 gap-x-4' : 'grid-cols-1'}`}>
            <div className='pb-6'>
              <InputControl
                label={`${isMfaSetup ? 'Authentication code 1' : 'Authentication code'}`}
                type="text"
                name='firstCode'
                placeholder="Enter code"
                value={formik.values.firstCode}
                customOnChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showError={formik.touched.firstCode && formik.errors.firstCode}
                errorText={formik.errors.firstCode}
              />
            </div>
            {isMfaSetup && (
              <div className='pb-6'>
                <InputControl
                  label='Authentication code 2'
                  type="text"
                  name='secondCode'
                  placeholder="Enter code"
                  value={formik.values.secondCode}
                  customOnChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  showError={formik.touched.secondCode && formik.errors.secondCode}
                  errorText={formik.errors.secondCode}
                />
              </div>
            )}
          </div>
          <InteractiveButton
            text='Please Log In'
            submit
            disabled={isDisabledBtn}
            size={ElementSize.XL}
            className="w-full"
          />
        </form>
      ) : (<SpinnerLoader />)}
    </div>
  )
}

const mapState = (state: RootState) => ({
  mfaSetupData: state.auth.mfaSetupData,
  mfaCheckData: state.auth.mfaCheckData,
  isAuthLoading: state.auth.isAuthLoading
});

const mapDispatch = (dispatch: Dispatch) => ({
  verifyMfa: dispatch.auth.verifyMfa,
  checkMfa: dispatch.auth.checkMfa
});

export default connect(mapState, mapDispatch)(MfaForm);
